<template>
  <v-dialog v-model="show" max-width="600px" persistent overlay-opacity="0.65">
    <v-card dark class="SiteAppsForgeAppConfiguration">
      <v-card-title class="headline">
        {{ app?.forge_app?.name }} Configuration
        <v-spacer></v-spacer>
        <v-btn icon text color="n3" @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text>
        <div class="d-flex align-center mb-4">
          <v-switch
            v-model="isEnabled"
            :label="isEnabled ? 'Enabled' : 'Disabled'"
            @change="toggleApp"
            color="y1"
          ></v-switch>
        </div>
        
        <div v-if="isEnabled && configFields.length > 0">
          <v-divider class="mb-4"></v-divider>
          <h3 class="mb-3 configuration-title">Configuration</h3>
          
          <div v-for="field in configFields" :key="field.name" class="mb-4">
            <v-text-field
              v-model="config[field.name]"
              :label="field.label"
              :placeholder="field.description"
              :required="field.required"
              :type="field.type === 'password' ? 'password' : 'text'"
              outlined
              dense
              dark
              class="config-input"
            ></v-text-field>
            <small v-if="field.description" class="text-caption text-grey">
              {{ field.description }}
            </small>
          </div>
          
          <v-divider class="my-4"></v-divider>
          
          <div class="d-flex gap-2">
            <v-btn
              @click="testSnippet"
              color="secondary"
              outlined
              :loading="testingSnippet"
            >
              <v-icon left>code</v-icon>
              Test Snippet
            </v-btn>
          </div>
        </div>
        
        <div v-else-if="isEnabled && configFields.length === 0">
          <v-alert type="info" class="mt-4">
            This app doesn't require any configuration.
          </v-alert>
        </div>
      </v-card-text>
      
      <v-card-actions class="modal-footer">
        <v-spacer></v-spacer>
        <v-btn @click="close" text color="n3">Cancel</v-btn>
        <v-btn
          @click="saveConfig"
          color="y1"
          :loading="saving"
          :disabled="!isEnabled && !hasChanges"
        >
          Save Configuration
        </v-btn>
      </v-card-actions>
    </v-card>
    
    <!-- Snippet Preview Dialog -->
    <v-dialog v-model="showSnippetModal" max-width="800px" overlay-opacity="0.65">
      <v-card dark class="SiteAppsForgeAppConfiguration">
        <v-card-title>
          Snippet Preview
          <v-spacer></v-spacer>
          <v-btn icon text color="n3" @click="showSnippetModal = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <pre><code class="language-html">{{ renderedSnippet }}</code></pre>
        </v-card-text>
        <v-card-actions class="modal-footer">
          <v-spacer></v-spacer>
          <v-btn @click="showSnippetModal = false" color="y1">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'SiteAppsForgeAppConfiguration',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    app: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isEnabled: false,
      config: {},
      saving: false,
      testingSnippet: false,
      showSnippetModal: false,
      renderedSnippet: '',
      originalConfig: {}
    }
  },
  computed: {
    ...mapState({
      activeSite: (state) => state.sites.active,
    }),
    configFields() {
      if (!this.app?.forge_app?.config_schema) {
        return []
      }
      
      // Parse config_schema if it's a JSON string
      let configSchema
      try {
        configSchema = typeof this.app.forge_app.config_schema === 'string' 
          ? JSON.parse(this.app.forge_app.config_schema) 
          : this.app.forge_app.config_schema
      } catch (error) {
        console.warn('Failed to parse config_schema:', error)
        return []
      }
      
      if (!configSchema?.properties) {
        return []
      }
      
      return Object.entries(configSchema.properties).map(([name, schema]) => ({
        name,
        label: schema.title || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        type: schema.type === 'string' ? 'text' : schema.type,
        description: schema.description,
        required: configSchema.required?.includes(name) || false
      }))
    },
    hasChanges() {
      return JSON.stringify(this.config) !== JSON.stringify(this.originalConfig) ||
             this.isEnabled !== this.app?.enabled
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.app) {
        this.loadAppData()
      }
    }
  },
  methods: {
    loadAppData() {
      this.isEnabled = this.app?.enabled || false
      this.config = { ...this.app?.config } || {}
      this.originalConfig = { ...this.config }
    },
    async toggleApp() {
      if (!this.app?.forge_app_id || !this.activeSite?.id) {
        this.$root.$emit('showSnack', {
          message: 'Unable to update app - missing data',
          color: 'error'
        })
        return
      }
      
      try {
        await this.$store.dispatch('forgeApps/updateAppSetting', {
          siteId: this.activeSite.id,
          appId: this.app.forge_app_id,
          enabled: this.isEnabled,
          config: this.config
        })
        this.$root.$emit('showSnack', {
          message: `App ${this.isEnabled ? 'enabled' : 'disabled'}`,
          color: 'success'
        })
      } catch (error) {
        this.$root.$emit('showSnack', {
          message: 'Failed to update app status',
          color: 'error'
        })
        this.isEnabled = !this.isEnabled // Revert
      }
    },
    async saveConfig() {
      if (!this.app?.forge_app_id || !this.activeSite?.id) {
        this.$root.$emit('showSnack', {
          message: 'Unable to save configuration - missing data',
          color: 'error'
        })
        return
      }
      
      this.saving = true
      try {
        await this.$store.dispatch('forgeApps/updateAppSetting', {
          siteId: this.activeSite.id,
          appId: this.app.forge_app_id,
          enabled: this.isEnabled,
          config: this.config
        })
        this.originalConfig = { ...this.config }
        this.$root.$emit('showSnack', {
          message: 'Configuration saved successfully',
          color: 'success'
        })
        this.$emit('app-changed')
      } catch (error) {
        this.$root.$emit('showSnack', {
          message: 'Failed to save configuration',
          color: 'error'
        })
      } finally {
        this.saving = false
      }
    },
    async testSnippet() {
      if (!this.app?.forge_app_id) {
        this.$root.$emit('showSnack', {
          message: 'Unable to test snippet - missing app data',
          color: 'error'
        })
        return
      }
      
      this.testingSnippet = true
      try {
        const result = await this.$store.dispatch('forgeApps/testAppSnippet', {
          appId: this.app.forge_app_id,
          config: this.config
        })
        this.renderedSnippet = result.rendered_snippet || 'No snippet available'
        this.showSnippetModal = true
      } catch (error) {
        this.$root.$emit('showSnack', {
          message: 'Failed to test snippet',
          color: 'error'
        })
      } finally {
        this.testingSnippet = false
      }
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="scss">
$style: SiteAppsForgeAppConfiguration;

.#{$style} {
  &.theme--dark.v-card {
    @extend %modal-body-styles;
    min-width: 600px;
    padding: 24px 24px 0;
    background: rgba(37, 36, 55, 0.9) !important;
    border: 1px solid $N5 !important;
    box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.25) !important;
    backdrop-filter: blur(10px) !important;
    border-radius: 8px !important;
  }
  
  .v-card__title {
    font-weight: 500;
    font-size: $H16;
    color: $white;
    margin-bottom: 16px;
    border-bottom: 1px solid $N5;
    padding-bottom: 16px;
  }
  
  .v-card__text {
    color: $white;
  }
  
  .configuration-title {
    font-weight: 500;
    font-size: $H14;
    color: $white;
  }
  
  .config-input {
    &.v-text-field--filled.v-input--dense > .v-input__control > .v-input__slot {
      @extend %input-styles;
    }
    &.v-input input {
      color: $white;
    }
    &.v-input .v-label {
      color: $N2;
    }
  }
  
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 0;
    gap: 16px;
    border-top: 1px solid $N5;
    margin-top: 16px;
    
    & > .theme--dark.v-btn {
      color: $white;
    }
  }
  
  .v-switch {
    .v-input__slot {
      .v-label {
        color: $white;
      }
    }
  }
  
  .v-divider {
    border-color: $N5;
  }
  
  .v-alert {
    background: rgba($B1, 0.1) !important;
    border: 1px solid rgba($B1, 0.3) !important;
    color: $white;
  }
}

pre {
  background: $N4;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.4;
  border: 1px solid $N5;
  color: $white;
}

code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  color: $white;
}
</style> 